// 🚀 최적화된 Image Utils

// 🔥 고품질 배경제거용 Worker (필요시에만 사용)
const createImageWorker = () => {
  const workerCode = `
    self.onmessage = function(e) {
      const { imageData, backgroundColor, width, height } = e.data;
      const data = imageData.data;
      
      // RGB 색상 파싱
      const hex = backgroundColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      
      // 🔥 개선된 배경색상 적용 - 반투명 픽셀도 고려
      for (let i = 0; i < data.length; i += 4) {
        const alpha = data[i + 3];
        
        if (alpha === 0) {
          // 완전 투명한 픽셀
          data[i] = r;
          data[i + 1] = g;
          data[i + 2] = b;
          data[i + 3] = 255;
        } else if (alpha < 255) {
          // 반투명 픽셀 처리 - 배경색과 블렌딩
          const alphaRatio = alpha / 255;
          const bgRatio = 1 - alphaRatio;
          
          data[i] = Math.round(data[i] * alphaRatio + r * bgRatio);
          data[i + 1] = Math.round(data[i + 1] * alphaRatio + g * bgRatio);
          data[i + 2] = Math.round(data[i + 2] * alphaRatio + b * bgRatio);
          data[i + 3] = 255;
        }
      }
      
      self.postMessage({ imageData: { data, width, height } });
    };
  `;
  
  const blob = new Blob([workerCode], { type: 'application/javascript' });
  return new Worker(URL.createObjectURL(blob));
};

// 🔥 배경제거용 Worker 풀 관리 (필요시에만 사용)
const workerPool = [];
const MAX_WORKERS = 1; // 배경제거 품질을 위해 Worker 사용 최소화

const getWorker = () => {
  if (workerPool.length > 0) {
    return workerPool.pop();
  }
  return createImageWorker();
};

const releaseWorker = (worker) => {
  if (workerPool.length < MAX_WORKERS) {
    workerPool.push(worker);
  } else {
    worker.terminate();
  }
};

// 🎯 고품질 배경색상 적용 함수 (성능과 품질 균형)
export const applyBackgroundColor = async (imageFile, backgroundColor) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    img.onload = () => {
      // 🔥 원본 크기 유지 - 배경제거 품질 최우선
      canvas.width = img.width;
      canvas.height = img.height;
      
      // 그라데이션인지 확인 (linear-gradient로 시작하는지)
      if (backgroundColor.startsWith('linear-gradient')) {
        // 그라데이션 파싱 및 적용
        const gradientMatch = backgroundColor.match(/linear-gradient\(([^)]+)\)/);
        if (gradientMatch) {
          const gradientStr = gradientMatch[1];
          const parts = gradientStr.split(',').map(s => s.trim());
          
          // 첫 번째 부분이 각도인지 확인
          let angle = 0;
          let colorStops = parts;
          if (parts[0].includes('deg')) {
            angle = parseFloat(parts[0]);
            colorStops = parts.slice(1);
          }
          
          // 각도를 라디안으로 변환 및 시작/끝점 계산
          const radians = (angle * Math.PI) / 180;
          const x1 = canvas.width / 2 - Math.sin(radians) * canvas.width / 2;
          const y1 = canvas.height / 2 + Math.cos(radians) * canvas.height / 2;
          const x2 = canvas.width / 2 + Math.sin(radians) * canvas.width / 2;
          const y2 = canvas.height / 2 - Math.cos(radians) * canvas.height / 2;
          
          const gradient = ctx.createLinearGradient(x1, y1, x2, y2);
          
          // 색상 정지점 추가
          colorStops.forEach((stop, index) => {
            const colorMatch = stop.trim().match(/^(#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3}|[a-zA-Z]+)/);
            if (colorMatch) {
              const position = index / (colorStops.length - 1);
              gradient.addColorStop(position, colorMatch[1]);
            }
          });
          
          ctx.fillStyle = gradient;
        } else {
          ctx.fillStyle = '#ffffff'; // 기본값
        }
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // 🎯 고품질 이미지 렌더링
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0);
        
        canvas.toBlob((blob) => {
          if (blob) {
            const file = new File([blob], `background-${Date.now()}.jpg`, { type: 'image/jpeg' });
            resolve(file);
          } else {
            reject(new Error('이미지 생성 실패'));
          }
        }, 'image/jpeg', 0.95); // 🔥 품질 95%로 상향
      } else {
        // 단색 배경 - 고품질 처리 우선
        // 🎯 고품질 이미지 렌더링
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0);
        
        // 🔥 Worker 대신 메인 스레드 처리 - 배경제거 품질 우선
        processImageSyncHighQuality(ctx, canvas, backgroundColor, resolve, reject);
      }
    };
    
    img.onerror = () => reject(new Error('이미지 로드 실패'));
    img.src = URL.createObjectURL(imageFile);
  });
};

// 🔥 고품질 동기 처리 (배경제거 품질 최우선)
const processImageSyncHighQuality = (ctx, canvas, backgroundColor, resolve, reject) => {
  try {
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    // RGB 색상 파싱
    const hex = backgroundColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // 🎯 개선된 배경색상 적용 - 반투명 픽셀도 고려
    for (let i = 0; i < data.length; i += 4) {
      const alpha = data[i + 3];
      
      if (alpha === 0) {
        // 완전 투명한 픽셀
        data[i] = r;
        data[i + 1] = g;
        data[i + 2] = b;
        data[i + 3] = 255;
      } else if (alpha < 255) {
        // 🔥 반투명 픽셀 처리 - 배경색과 블렌딩
        const alphaRatio = alpha / 255;
        const bgRatio = 1 - alphaRatio;
        
        data[i] = Math.round(data[i] * alphaRatio + r * bgRatio);
        data[i + 1] = Math.round(data[i + 1] * alphaRatio + g * bgRatio);
        data[i + 2] = Math.round(data[i + 2] * alphaRatio + b * bgRatio);
        data[i + 3] = 255;
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
    
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `background-${Date.now()}.jpg`, { type: 'image/jpeg' });
        resolve(file);
      } else {
        reject(new Error('이미지 생성 실패'));
      }
    }, 'image/jpeg', 0.95); // 🔥 품질 95%로 상향
  } catch (error) {
    reject(error);
  }
};

// 기존 동기 처리 함수 (호환성 유지)
const processImageSync = (ctx, canvas, backgroundColor, resolve, reject) => {
  processImageSyncHighQuality(ctx, canvas, backgroundColor, resolve, reject);
};

// 🔥 고품질 Worker 처리 (매우 큰 이미지용만)
const processImageWithWorker = (ctx, canvas, backgroundColor, resolve, reject) => {
  const worker = getWorker();
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  
  worker.onmessage = (e) => {
    try {
      const processedImageData = new ImageData(
        new Uint8ClampedArray(e.data.imageData.data),
        e.data.imageData.width,
        e.data.imageData.height
      );
      
      ctx.putImageData(processedImageData, 0, 0);
      
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], `background-${Date.now()}.jpg`, { type: 'image/jpeg' });
          resolve(file);
        } else {
          reject(new Error('이미지 생성 실패'));
        }
        releaseWorker(worker);
      }, 'image/jpeg', 0.95); // 🔥 품질 95%로 상향
    } catch (error) {
      reject(error);
      releaseWorker(worker);
    }
  };
  
  worker.onerror = (error) => {
    reject(error);
    releaseWorker(worker);
  };
  
  // Worker에 데이터 전송
  worker.postMessage({
    imageData: {
      data: Array.from(imageData.data),
      width: imageData.width,
      height: imageData.height
    },
    backgroundColor,
    width: canvas.width,
    height: canvas.height
  });
};

// 🔥 고품질 흑백효과 적용
export const applyGrayscaleFilter = async (imageFile) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    img.onload = () => {
      // 🔥 원본 크기 유지 - 흑백 품질 최우선
      canvas.width = img.width;
      canvas.height = img.height;
      
      // 🎯 고품질 이미지 렌더링
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      // 원본 이미지 먼저 그리기
      ctx.drawImage(img, 0, 0);
      
      // 🔥 픽셀 단위 흑백 처리 - 더 정확한 색상 변환
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      for (let i = 0; i < data.length; i += 4) {
        // 표준 RGB to Grayscale 공식 사용
        const grayscale = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
        data[i] = grayscale;     // Red
        data[i + 1] = grayscale; // Green
        data[i + 2] = grayscale; // Blue
        // Alpha는 그대로 유지
      }
      
      ctx.putImageData(imageData, 0, 0);
      
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], `grayscale-${Date.now()}.jpg`, { type: 'image/jpeg' });
          resolve(file);
        } else {
          reject(new Error('흑백 변환 실패'));
        }
      }, 'image/jpeg', 0.95); // 🔥 품질 95%로 상향
    };
    
    img.onerror = () => reject(new Error('이미지 로드 실패'));
    img.src = URL.createObjectURL(imageFile);
  });
};

// 이미지 품질 최적화 함수
export const optimizeImageQuality = async (imageFile, options = {}) => {
  const {
    maxWidth = 800,
    maxHeight = 800,
    quality = 0.8,
    format = 'image/jpeg'
  } = options;
  
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    img.onload = () => {
      // 비율 유지한 리사이징
      const scale = Math.min(
        maxWidth / img.width,
        maxHeight / img.height,
        1 // 원본보다 크게 하지 않음
      );
      
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      
      // 🚀 고품질 리샘플링
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      canvas.toBlob((blob) => {
        if (blob) {
          const optimizedFile = new File([blob], `optimized-${Date.now()}.jpg`, { type: format });
          resolve(optimizedFile);
        } else {
          reject(new Error('이미지 최적화 실패'));
        }
      }, format, quality);
    };
    
    img.onerror = () => reject(new Error('이미지 로드 실패'));
    img.src = URL.createObjectURL(imageFile);
  });
};

// 메모리 정리 함수
export const cleanupImageUtils = () => {
  // Worker 풀 정리
  workerPool.forEach(worker => worker.terminate());
  workerPool.length = 0;
};

// 이미지 사전 로딩 함수 (미리보기 성능 향상)
export const preloadImages = async (imageUrls) => {
  const promises = imageUrls.map(url => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  });
  
  return Promise.allSettled(promises);
};

// 이미지 크기 검증 함수
export const validateImageSize = (file, maxSizeMB = 10) => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

// 이미지 형식 검증 함수
export const validateImageFormat = (file) => {
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
  return allowedTypes.includes(file.type);
};

// 이미지 타입명 반환 함수 - 사용자 친화적인 이름으로 변경
export const getImageTypeName = (type) => {
  switch (type) {
    case 'original': return '원본';
    case 'gfpgan': return 'AI 화질개선';
    case 'gfpgan-bg-remover': return 'AI 배경제거';
    case 'gfpgan-bg-remover-bw': return 'AI 흑백효과';
    case 'final': return 'AI 완성본';
    default: return 'AI 완성본';
  }
};
